<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesPendingChargePayerHeader from './SalesPendingChargePayerHeader.vue';
import SalesPendingChargePayerRows from './SalesPendingChargePayerRows.vue';

const props = defineProps({
  charge: { type: Object, required: true },
  loading: { type: Boolean, default: false },
});
const emit = defineEmits(['save']);
const { t } = useI18n();
const rows = ref([]);
const count = ref(1);

const options = computed(() => props.charge.compradores_vendedores || []);
const installment = computed(() => props.charge.dados_parcela || {});
const commission = computed(() => props.charge.dados_comissao_geral || {});
const installmentCents = computed(
  () => installment.value.valor_parcela?.amount_cents || 0
);

const parseCents = value => {
  if (typeof value === 'number') return Math.round(value * 100);
  const source = String(value?.amount ?? value ?? '').replace(/[^\d,.-]/g, '');
  if (!source) return 0;
  const normalized = source.includes(',')
    ? source.replace(/\./g, '').replace(',', '.')
    : source;
  return Math.round(Number(normalized) * 100) || 0;
};

const formatCents = cents =>
  `R$ ${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.max(cents, 0) / 100)}`;

const selectedCents = computed(() =>
  rows.value.reduce((total, row) => total + parseCents(row.valor_pagamento), 0)
);
const mismatch = computed(() => selectedCents.value !== installmentCents.value);
const valid = computed(
  () =>
    options.value.length > 0 &&
    rows.value.length > 0 &&
    rows.value.every(
      row =>
        row.property_transaction_party_id && parseCents(row.valor_pagamento) > 0
    ) &&
    !mismatch.value
);

const emptyRow = () => ({
  property_transaction_party_id: '',
  valor_pagamento: '',
});

const distribute = () => {
  const divisor = rows.value.length || 1;
  const base = Math.floor(installmentCents.value / divisor);
  const remainder = installmentCents.value - base * divisor;
  rows.value = rows.value.map((row, index) => ({
    ...row,
    valor_pagamento: formatCents(
      base + (index === divisor - 1 ? remainder : 0)
    ),
  }));
};

const setCount = value => {
  const nextCount = Math.max(
    1,
    Math.min(Number(value) || 1, options.value.length || 1)
  );
  const current = rows.value.slice(0, nextCount);
  while (current.length < nextCount) current.push(emptyRow());
  count.value = nextCount;
  rows.value = current;
  distribute();
};

const reset = () => {
  const current = installment.value.responsaveis_pagamento || [];
  count.value = Math.max(
    1,
    Math.min(current.length || 1, options.value.length || 1)
  );
  rows.value = current.length
    ? current.map(row => ({
        property_transaction_party_id:
          row.responsavel_id || row.usuario_id || '',
        valor_pagamento: formatCents(parseCents(row.valor_pagamento)),
      }))
    : [emptyRow()];
  if (!current.length) distribute();
};

const updateRow = ({ index, field, value }) => {
  rows.value = rows.value.map((row, rowIndex) =>
    rowIndex === index ? { ...row, [field]: value } : row
  );
};

const save = () => {
  if (!valid.value) return;
  emit(
    'save',
    rows.value.map(row => ({
      property_transaction_party_id: Number(row.property_transaction_party_id),
      valor_pagamento: row.valor_pagamento,
    }))
  );
};

watch(() => props.charge, reset, { immediate: true, deep: true });
</script>

<template>
  <section class="rounded-lais border border-lais-border-soft bg-white p-5">
    <SalesPendingChargePayerHeader
      :installment-label="formatCents(installmentCents)"
      :commission-label="
        formatCents(commission.valor_comissao_liquida?.amount_cents || 0)
      "
      :current-label="formatCents(selectedCents)"
      :expected-label="formatCents(installmentCents)"
      :show-mismatch="mismatch && rows.length > 0"
    />

    <div class="mt-5 grid gap-4">
      <SalesPendingChargePayerRows
        :count="count"
        :loading="loading"
        :options="options"
        :rows="rows"
        @change-count="setCount"
        @update-row="updateRow"
      />
    </div>
  </section>

  <footer
    class="fixed inset-x-0 bottom-0 z-20 flex justify-end border-t border-lais-border bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-lais-pop sm:px-6 lg:px-10"
  >
    <LaisPrimaryButton
      size="lg"
      icon="i-lucide-check"
      :disabled="!valid"
      :is-loading="loading"
      @click="save"
    >
      {{ t('SALES.PENDING_CHARGE.PAYER_EDITOR.SAVE') }}
    </LaisPrimaryButton>
  </footer>
</template>
